import { Component, Input } from '@angular/core';
import { AddMultipleComponent, IAddedItem } from 'components/input/add-multiple/add-multiple.component';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { TagsComponent } from 'components/input/tags/tags.component';
import { QuestionTagConstants } from 'constants/QuestionTagConstants';
import { InputType } from 'enums/InputType';
import { QuestionCategoryType } from 'enums/QuestionCategoryType';
import { ReplyType } from 'enums/ReplyType';
import { QuestionModel } from 'models/generated/Entities.GormCrm.Question';
import { QuestionnaireQuestionModel } from 'models/generated/Entities.GormCrm.QuestionnaireQuestion';
import { QuestionReplyOptionModel } from 'models/generated/Entities.GormCrm.QuestionReplyOption';
import { QuestionTagModel } from 'models/generated/Entities.GormCrm.QuestionTag';
import { SelectListItemModel } from 'models/SelectList';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { TranslatePipe } from 'pipes/translate.pipe';
import { FormValidationService } from 'services/form-validation.service';
import { ProductService } from 'services/product.service';
import { QuestionnaireService } from 'services/questionnaire.service';

@Component({
	selector: 'edit-questionnaire-question',
	templateUrl: './edit-questionnaire-question.component.html',
	providers: [FormValidationService],
	imports: [Layout, CustomSelectComponent, AddMultipleComponent, CustomInputComponent, TagsComponent, ListView, TranslatePipe]
})
export class EditQuestionnaireQuestionComponent {
	inputTypeEnum = InputType;
	CategoryType = QuestionCategoryType;
	ReplyType = ReplyType;
	question?: QuestionModel;

	productItems: IAddedItem[] = [];
	addedProducts: IAddedItem[] = [];
	replyTypeList: SelectListItemModel[] = [];
	questionCategoryTypeList: SelectListItemModel[] = [];
	replyOptionList = new QuestionReplyOptionModel();
	replyOptionTypes: SelectListItemModel[] = [];
	questionTags: QuestionTagModel[];

	enableSave = false;

	private _questionnaireQuestion: QuestionnaireQuestionModel = new QuestionnaireQuestionModel;
	@Input()
	set questionnaireQuestion(questionnaireQuestion: QuestionnaireQuestionModel) {
		this.question = undefined;
		this._questionnaireQuestion = questionnaireQuestion;

		this.productItems = [];
		this.addedProducts = [];
		this.replyTypeList = [];

		this.getReplyTypes();
		this.getCategoryType();

		this.productService.getAllActiveProducts().then(products => {
			this.productItems = products.map((product) => { return { value: product.id!, label: product.name }; });
			if (this._questionnaireQuestion.questionId) {
				this.questionnaireService.getQuestionById(this._questionnaireQuestion.questionId!).then(question => {
					this.question = question;
					if (this.question.category == QuestionCategoryType.Product && this.question.questionnaireQuestionProducts.length > 0) {
						this.setupAddedProducts();
					}
				});
			} else {
				this.question = new QuestionModel();
				this.question.requiredNumberOfReplies = 1;
			}
		});
		this.enableSave = true;
	}
	get questionnaireQuestion() {
		return this._questionnaireQuestion;
	}

	constructor(
		private readonly translate: TranslatePipe,
		private readonly questionnaireService: QuestionnaireService,
		private readonly productService: ProductService) {

		this.questionTags = QuestionTagConstants.values.map(tag => ({ tag } as QuestionTagModel));
	}

	setupAddedProducts() {
		const addedProducts: IAddedItem[] = [];
		if (!this.question) return;
		this.question.questionnaireQuestionProducts.forEach(questionnaireQuestionProduct => {
			let addedItem = this.productItems.find(pi => pi.value === questionnaireQuestionProduct.productId);
			if (addedItem) addedProducts.push(addedItem);
		});
		this.addedProducts = addedProducts;
	}

	isMultipleAnswersPossible() {
		return this.question?.questionReplyInputType !== undefined && [ReplyType.Text, ReplyType.Number, ReplyType.Editor].indexOf(this.question.questionReplyInputType) >= 0;
	}

	addOptionValue() {
		if (!this.question) return;
		this.question.questionReplyOptions.push(new QuestionReplyOptionModel());
	}

	removeOptionValue(index: number) {
		if (!this.question) return;
		this.question.questionReplyOptions.splice(index, 1);
	}

	getCategoryType() {
		let questionCategoryTypeList: SelectListItemModel[] = [];
		questionCategoryTypeList.push(new SelectListItemModel(QuestionCategoryType.Company, this.translate.transform('enum.question-category-type.Company')));
		questionCategoryTypeList.push(new SelectListItemModel(QuestionCategoryType.Order, this.translate.transform('enum.question-category-type.Order')));
		questionCategoryTypeList.push(new SelectListItemModel(QuestionCategoryType.Product, this.translate.transform('enum.question-category-type.Product')));
		this.questionCategoryTypeList = questionCategoryTypeList;
	}

	getReplyTypes() {
		let replyTypeList: SelectListItemModel[] = [];
		ReplyType.getKeys().forEach((value: string, key: ReplyType) => {
			replyTypeList.push(new SelectListItemModel(key, this.translate.transform('enum.question-reply-type.' + value)));
		});
		this.replyTypeList = replyTypeList;
	}

	saveQuestion() {
		if (this.question) {
			this.enableSave = false;
			if (!this.isMultipleAnswersPossible()) this.question.requiredNumberOfReplies = 1;
			let relatedProducts = this.question.category == QuestionCategoryType.Product ? this.addedProducts.map((item) => { return item.value }) : [];
			this.questionnaireService.saveQuestion(this.question, this.questionnaireQuestion.questionnaireId as string, relatedProducts);
		}
	}
}
